import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { CheckoutStore } from '@seatix/checkout-data-access';
import { EMPTY_ORDER_DISPLAY, orderDisplay } from '@seatix/checkout-ui';
import { injectBreakpoint } from '@seatix/shared-config';
import { EventHeaderInfoComponent } from '@seatix/shared-event-ui';
import {
  SessionExpirationDialogComponent,
  SessionExpirationDialogData,
} from '@seatix/shared-ui-session-expiration-dialog';
import { SharedUiCountdownComponent } from '@seatix/shared/ui/ui-countdown';
import { LogoComponent } from '@seatix/shared/ui/ui-logo';
import { CheckoutCountdownComponent } from '../checkout-countdown/checkout-countdown.component';

@Component({
  selector: 'stx-checkout-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    LogoComponent,
    MatToolbar,
    MatToolbarRow,
    EventHeaderInfoComponent,
    MatDialogModule,
    CheckoutCountdownComponent,
  ],
  host: {
    class: 'block border-b border-outline-variant/30',
  },
  styles: `
    @reference '../../../../../shared/theming/src/lib/tailwind/reference.css';

    :host {
      .mat-toolbar-row {
        @apply flex gap-4 xl:container flex-row xl:mx-auto;
      }
    }
  `,
  template: `
    @if (order()) {
      <mat-toolbar>
        <mat-toolbar-row>
          <div class="shrink-0 place-content-center">
            @if (screenMd()) {
              <stx-ui-logo [size]="'sm'" />
            } @else {
              <stx-ui-logo
                variant="isotype"
                [size]="'sm'"
              />
            }
          </div>

          <div class="flex flex-1 flex-row">
            <div class="flex h-auto min-h-0 flex-1 place-items-center">
              <stx-event-header-info
                [title]="display().title"
                [dateText]="display().dateLine"
                [venueName]="display().venueName"
              />
            </div>
            @if (screenMd()) {
              <div
                class="flex flex-row place-content-center place-items-center gap-2 place-self-center"
              >
                <stx-checkout-countdown />
              </div>
            }
          </div>
        </mat-toolbar-row>
      </mat-toolbar>
    }
  `,
})
export class CheckoutHeaderComponent {
  screenMd = injectBreakpoint('md');
  #store = inject(CheckoutStore);
  order = this.#store.order;
  protected readonly display = computed(() => {
    const order = this.order();
    return order ? orderDisplay(order) : EMPTY_ORDER_DISPLAY;
  });
  orderLoading = this.#store.orderLoading;
  #dialog = inject(MatDialog);

  readonly countdown = viewChild(SharedUiCountdownComponent);

  constructor() {
    effect(() => {
      /* if (paymentReference && countdownComponent) {
        countdownComponent.stop();
      } */
    });
  }

  onCountdownFinished() {
    if (this.#store.pendingExternalRedirect()) {
      return;
    }

    if (!this.orderLoading()) {
      const order = this.order();
      if (order) {
        const display = orderDisplay(order);
        const dialogData: SessionExpirationDialogData = {
          target: {
            name: display.title || null,
            kind: display.kind === 'season' ? 'season' : 'event',
          },
        };
        this.#dialog
          .open(SessionExpirationDialogComponent, {
            data: { ...dialogData },
          })
          .afterClosed()
          .subscribe(() => {
            if (this.#store.pendingExternalRedirect()) {
              return;
            }
            this.#store.handleExpiration();
            this.#store.clearOrder();
          });
      }
    }
  }
}
